import {
  CreateInterviewWithStripePaymentIntentDto,
  CV,
  FullCV,
  FullCVWithTemplate,
  Interview,
} from '@cibli/shared';
import { AxiosRequestConfig } from 'axios';

import api from './api';

// Logger for interview service
const logger = {
  warn: (message: string, ...args: unknown[]) => console.warn(`[interview] ${message}`, ...args),
  error: (message: string, ...args: unknown[]) => console.error(`[interview] ${message}`, ...args),
};

export const createInterview = async (
  userId: string,
  stripePaymentIntentId?: string,
): Promise<Interview> => {
  return await api.post<Interview, CreateInterviewWithStripePaymentIntentDto>(
    '/interviews/with-stripe-payment-intent',
    {
      interview: {
        user: {
          connect: {
            id: userId,
          },
        },
      },
      stripePaymentIntentId,
    },
  );
};

export const generateCV = async (interviewId: string, cvDto: FullCVWithTemplate): Promise<CV> => {
  return await api.post<CV, FullCVWithTemplate>(`/interviews/${interviewId}/cv`, cvDto);
};

export const fetchCV = async (id: string): Promise<FullCVWithTemplate> => {
  return await api.get<FullCVWithTemplate>(`/cv/${id}`);
};

export const downloadCV = async (id: string): Promise<Blob> => {
  try {
    const config: AxiosRequestConfig = {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf',
      },
    };

    const url = `/cv/${id}/download`;
    const response = await api.get<Blob>(url, config);

    // Verify that we received a PDF blob
    if (response.type !== 'application/pdf') {
      logger.warn(`Unexpected content type: ${response.type}`);
    }

    return response;
  } catch (error) {
    logger.error('Error downloading CV:', error);
    throw error;
  }
};

export const sendCVBySMS = async (
  id: string,
  options?: { showErrorToast?: boolean },
): Promise<{ status: 'ok' }> => {
  return await api.post<{ status: 'ok' }, void>(`/cv/${id}/send-sms`, undefined, options);
};

export const sendCVByEmail = async (
  id: string,
  options?: { showErrorToast?: boolean; email?: string },
): Promise<{ status: 'ok' }> => {
  return await api.post<{ status: 'ok' }, { email?: string }>(
    `/cv/${id}/send-email`,
    options?.email ? { email: options.email } : {},
    options,
  );
};

export const updateUserEmail = async (
  email: string,
): Promise<{ status: 'ok'; user: { id: string; email: string } }> => {
  return await api.patch<{ status: 'ok'; user: { id: string; email: string } }, { email: string }>(
    '/users/me/email',
    { email },
  );
};

export interface GenderizeCvPayload {
  cv: FullCV;
  gender: 'homme' | 'femme';
  userId?: string;
  interviewId?: string;
}

export interface GenderizeCvResponse {
  cv: FullCV;
}

export const genderizeCv = async (payload: GenderizeCvPayload): Promise<GenderizeCvResponse> => {
  return api.post<GenderizeCvResponse, GenderizeCvPayload>('/cv/genderize', payload);
};

export const translateCV = async (
  id: string,
  options?: { targetLanguage?: string },
): Promise<{
  cvTranslated: CV;
  fullCvTranslated?: FullCV;
}> => {
  return await api.patch<
    { cvTranslated: CV; fullCvTranslated?: FullCV },
    { targetLanguage?: string }
  >(`/cv/${id}/translate`, { targetLanguage: options?.targetLanguage });
};
